import {
    type ReactNode,
    useState
} from "react";
import {
    useWindowDimensions,
    type ViewStyle,
    StyleSheet,
    View
} from "react-native";
import {
    WorkspaceSwitcher
} from "./brand";
import {
    type NCoreUIKitIcon,
    NCoreUIKitLocalize,
    type ChartConfig,
    NCoreUIKitTheme,
    RadialChart,
    LineChart,
    Separator,
    BarChart,
    Timeline,
    Sidebar,
    Avatar,
    Button,
    Alert,
    Badge,
    Table,
    Chart,
    Card,
    Tabs,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    EllipsisVertical,
    TriangleAlert,
    ShoppingCart,
    CircleCheck,
    Calendar,
    Download,
    FileText,
    UserPlus,
    Package,
    Wallet,
    Users,
    Clock,
    Undo2,
    Plus,
    Tag
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

type StockRow = {
    isUrgent: boolean;
    name: string;
    left: number;
    rate: number;
    days: number;
    sku: string;
};

const EllipsisVerticalIcon = createKitIcon(EllipsisVertical);

const ShoppingCartIcon = createKitIcon(ShoppingCart);

const TriangleAlertIcon = createKitIcon(TriangleAlert);

const CircleCheckIcon = createKitIcon(CircleCheck);

const CalendarIcon = createKitIcon(Calendar);

const DownloadIcon = createKitIcon(Download);

const PackageIcon = createKitIcon(Package);

const FileTextIcon = createKitIcon(FileText);

const UserPlusIcon = createKitIcon(UserPlus);

const WalletIcon = createKitIcon(Wallet);

const UsersIcon = createKitIcon(Users);

const ClockIcon = createKitIcon(Clock);

const PlusIcon = createKitIcon(Plus);

const TagIcon = createKitIcon(Tag);

const UndoIcon = createKitIcon(Undo2);

const TABS = [
    "overview",
    "revenue",
    "expenses",
    "stock"
] as const;

const REVENUE = [
    262000,
    281000,
    274000,
    305000,
    322000,
    348000,
    366000,
    389000,
    412000,
    451200,
    468000,
    482000
];

const EXPENSE = [
    214000,
    221000,
    226000,
    238000,
    246000,
    259000,
    268000,
    281000,
    296000,
    312400,
    309000,
    318000
];

const ORDERS = [
    [
        212,
        58,
        26,
        9
    ],
    [
        226,
        62,
        28,
        10
    ],
    [
        238,
        66,
        30,
        9
    ],
    [
        251,
        70,
        34,
        12
    ],
    [
        262,
        74,
        32,
        11
    ],
    [
        279,
        80,
        36,
        13
    ],
    [
        291,
        78,
        38,
        12
    ],
    [
        305,
        84,
        40,
        14
    ]
];

const STOCK: Array<StockRow> = [
    {
        name: "blocks.dashboard02.productSantos",
        sku: "KK-BRZ-1000",
        isUrgent: true,
        rate: 3.2,
        left: 9,
        days: 3
    },
    {
        name: "blocks.dashboard02.productYirgacheffe",
        sku: "KK-ETY-250",
        isUrgent: false,
        rate: 4.5,
        left: 21,
        days: 5
    },
    {
        name: "blocks.dashboard02.productV60",
        sku: "KK-V60-MTL",
        isUrgent: false,
        rate: 1.9,
        left: 12,
        days: 6
    }
];

const FIRST_MONTH = new Date(2025, 11, 1);

const FIRST_WEEK = new Date(2026, 8, 7);

const WEEK = 7 * 86400000;

const stylesheet = StyleSheet.create({
    footer: {
        flexDirection: "column",
        alignItems: "center"
    },
    metric: {
        alignItems: "baseline",
        flexDirection: "row"
    },
    bar: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    group: {
        alignItems: "center",
        flexDirection: "row",
        flexShrink: 1
    },
    row: {
        flexDirection: "row"
    },
    chart: {
        width: "100%",
        flexGrow: 1
    },
    one: {
        minWidth: 0,
        flex: 1
    },
    two: {
        minWidth: 0,
        flex: 2
    },
    grow: {
        flexGrow: 1
    },
    content: {
        minWidth: 0
    }
});

const handlePress = () => undefined;

const Dashboard02 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        activePage,
        setActivePage
    ] = useState("finance");

    const [
        tab,
        setTab
    ] = useState<string>("overview");

    const currency = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: "currency",
        currency: "TRY"
    });

    const compactCurrency = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 1,
        notation: "compact",
        style: "currency",
        currency: "TRY"
    });

    const million = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 2,
        notation: "compact",
        style: "currency",
        currency: "TRY"
    });

    const decimal = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1
    });

    const integer = new Intl.NumberFormat(activeLocale);

    const monthFormat = new Intl.DateTimeFormat(activeLocale, {
        month: "short"
    });

    const dayFormat = new Intl.DateTimeFormat(activeLocale, {
        month: "short",
        day: "numeric"
    });

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isSplit = width >= shapeConfigs.wideBreakpoint;

    const isRoomy = width >= shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth;

    const lineRows = REVENUE.map((revenue, index) => ({
        date: new Date(FIRST_MONTH.getFullYear(), FIRST_MONTH.getMonth() + index, 1).toISOString(),
        expense: EXPENSE[index] ?? 0,
        revenue
    }));

    const orderRows = ORDERS.map(([
        delivered,
        shipping,
        preparing,
        returned
    ], index) => ({
        date: new Date(FIRST_WEEK.getTime() + index * WEEK).toISOString(),
        delivered: delivered ?? 0,
        preparing: preparing ?? 0,
        returned: returned ?? 0,
        shipping: shipping ?? 0
    }));

    const goalConfig: ChartConfig = {
        goal: {
            title: t("blocks.dashboard02.goalTitle"),
            colorIndex: 1
        }
    };

    const flowConfig: ChartConfig = {
        expense: {
            title: t("blocks.dashboard02.expense"),
            colorIndex: 2
        },
        revenue: {
            title: t("blocks.dashboard02.revenue"),
            colorIndex: 1
        }
    };

    const orderConfig: ChartConfig = {
        delivered: {
            title: t("blocks.dashboard02.delivered"),
            colorIndex: 1
        },
        preparing: {
            title: t("blocks.dashboard02.preparing"),
            colorIndex: 3
        },
        returned: {
            title: t("blocks.dashboard02.returned"),
            colorIndex: 4
        },
        shipping: {
            title: t("blocks.dashboard02.shipping"),
            colorIndex: 2
        }
    };

    const handlePage = (page: string) => () => setActivePage(page);

    const renderNavItem = (page: string, icon: NCoreUIKitIcon, title: string, count?: number) => <Sidebar.MenuItem
        key={page}
    >
        <Sidebar.MenuButton
            isActive={activePage === page}
            onPress={handlePage(page)}
            title={title}
            icon={icon}
        />
        {
            count ?
                <Sidebar.MenuBadge
                    count={count}
                />
            :
                null
        }
    </Sidebar.MenuItem>;

    const renderCards = (first: ReactNode, second: ReactNode, firstStyle: ViewStyle, secondStyle: ViewStyle) => <View
        style={[
            isSplit ? stylesheet.row : undefined,
            {
                gap: spaces.spacingMd
            }
        ]}
    >
        <View
            style={firstStyle}
        >
            {first}
        </View>
        <View
            style={secondStyle}
        >
            {second}
        </View>
    </View>;

    const renderStock = (row: StockRow) => <Table.Row
        key={row.sku}
    >
        <Table.Cell>
            <Text
                variant="labelMediumSize"
                color="high"
            >
                {t(row.name)}
            </Text>
        </Table.Cell>
        <Table.Cell>
            <Text
                variant="monoMediumSize"
                color="mid"
            >
                {row.sku}
            </Text>
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
        >
            {t("blocks.dashboard02.units", [
                integer.format(row.left)
            ])}
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
        >
            {t("blocks.dashboard02.perDay", [
                decimal.format(row.rate)
            ])}
        </Table.Cell>
        <Table.Cell>
            <Badge
                type={row.isUrgent ? "danger" : "warning"}
                title={t("blocks.dashboard02.days", [
                    row.days
                ])}
                icon={ClockIcon}
                variant="tonal"
            />
        </Table.Cell>
    </Table.Row>;

    return <Sidebar.Provider
        variant="inset"
    >
        <Sidebar
            collapseBehaviour={isRoomy ? "offcanvas" : "icon"}
            title={t("blocks.dashboard02.navTitle")}
        >
            <Sidebar.Header>
                <WorkspaceSwitcher
                    onPress={handlePress}
                />
            </Sidebar.Header>
            <Sidebar.Content>
                <Sidebar.Group>
                    <Sidebar.Menu>
                        {renderNavItem("finance", WalletIcon, t("blocks.dashboard02.navFinance"))}
                        {renderNavItem("orders", ShoppingCartIcon, t("blocks.dashboard02.navOrders"), 32)}
                        {renderNavItem("products", PackageIcon, t("blocks.dashboard02.navProducts"))}
                        {renderNavItem("customers", UsersIcon, t("blocks.dashboard02.navCustomers"))}
                    </Sidebar.Menu>
                </Sidebar.Group>
                <Sidebar.Group
                    title={t("blocks.dashboard02.accounting")}
                >
                    <Sidebar.Menu>
                        {renderNavItem("invoices", FileTextIcon, t("blocks.dashboard02.navInvoices"))}
                        {renderNavItem("prices", TagIcon, t("blocks.dashboard02.navPrices"))}
                    </Sidebar.Menu>
                </Sidebar.Group>
            </Sidebar.Content>
            <Sidebar.Footer>
                <Sidebar.Menu>
                    <Sidebar.MenuItem>
                        <Sidebar.MenuButton
                            renderIcon={() => <Avatar
                                title={t("blocks.dashboard02.userName")}
                                size="xSmall"
                            />}
                            subTitle={t("blocks.dashboard02.userEmail")}
                            title={t("blocks.dashboard02.userName")}
                        />
                        <Sidebar.MenuAction
                            title={t("blocks.dashboard02.userMenu")}
                            icon={EllipsisVerticalIcon}
                            onPress={handlePress}
                            isShowOnHover={false}
                        />
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
            </Sidebar.Footer>
        </Sidebar>
        <Sidebar.Inset>
            <View
                style={[
                    stylesheet.bar,
                    {
                        paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        paddingVertical: spaces.spacingMd,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.group,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Sidebar.Trigger/>
                    <Text
                        variant="titleMediumSize"
                        color="high"
                    >
                        {t("blocks.dashboard02.navFinance")}
                    </Text>
                    <Badge
                        title={t("blocks.dashboard02.company")}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                    />
                </View>
                <View
                    style={stylesheet.grow}
                />
                <Button
                    title={isCompact ? undefined : t("blocks.dashboard02.period")}
                    accessibilityLabel={t("blocks.dashboard02.period")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    icon={CalendarIcon}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={isCompact ? undefined : t("blocks.dashboard02.export")}
                    accessibilityLabel={t("blocks.dashboard02.export")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    icon={DownloadIcon}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={isCompact ? undefined : t("blocks.dashboard02.createInvoice")}
                    accessibilityLabel={t("blocks.dashboard02.createInvoice")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="filled"
                    icon={PlusIcon}
                    type="primary"
                    size="small"
                />
            </View>
            <Separator/>
            <View
                style={[
                    stylesheet.content,
                    {
                        padding: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <Tabs
                    onValueChange={setTab}
                    variant="line"
                    value={tab}
                >
                    <Tabs.List
                        title={t("blocks.dashboard02.tabsTitle")}
                    >
                        {
                            TABS.map((key) => <Tabs.Trigger
                                title={t(`blocks.dashboard02.tab_${key}`)}
                                value={key}
                                key={key}
                            />)
                        }
                    </Tabs.List>
                </Tabs>
                {
                    renderCards(
                        <Chart
                            description={t("blocks.dashboard02.goalDescription")}
                            footer={<View
                                style={[
                                    stylesheet.footer,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <View
                                    style={[
                                        stylesheet.metric,
                                        {
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <Text
                                        variant="titleLargeSize"
                                        color="high"
                                    >
                                        {million.format(1360000)}
                                    </Text>
                                    <Text
                                        variant="bodyMediumSize"
                                        color="mid"
                                    >
                                        {`/ ${million.format(2000000)}`}
                                    </Text>
                                </View>
                                <Badge
                                    title={t("blocks.dashboard02.daysLeft", [
                                        24
                                    ])}
                                    spreadBehaviour="free"
                                    variant="outline"
                                    icon={ClockIcon}
                                    type="neutral"
                                />
                            </View>}
                            title={t("blocks.dashboard02.goalTitle")}
                            spreadBehaviour="stretch"
                            isAnimatedOnMount={true}
                            style={stylesheet.chart}
                            config={goalConfig}
                        >
                            <RadialChart
                                centerValue={new Intl.NumberFormat(activeLocale, {
                                    style: "percent"
                                }).format(0.68)}
                                categoryTitle={t("blocks.dashboard02.goalTitle")}
                                centerTitle={t("blocks.dashboard02.completed")}
                                variant="progress"
                                plotSize="medium"
                                valueKey="goal"
                                maxValue={100}
                                value={68}
                            />
                        </Chart>,
                        <Chart
                            description={t("blocks.dashboard02.flowDescription")}
                            title={t("blocks.dashboard02.flowTitle")}
                            spreadBehaviour="stretch"
                            isAnimatedOnMount={true}
                            style={stylesheet.chart}
                            config={flowConfig}
                        >
                            <LineChart
                                categoryFormatter={(value) => monthFormat.format(new Date(String(value)))}
                                valueFormatter={(value) => compactCurrency.format(value)}
                                categoryTitle={t("blocks.dashboard02.month")}
                                categoryKey="date"
                                plotSize="medium"
                                curve="natural"
                                data={lineRows}
                                seriesKeys={[
                                    "revenue",
                                    "expense"
                                ]}
                            />
                        </Chart>,
                        stylesheet.one,
                        stylesheet.two
                    )
                }
                {
                    renderCards(
                        <Chart
                            description={t("blocks.dashboard02.ordersDescription")}
                            title={t("blocks.dashboard02.ordersTitle")}
                            spreadBehaviour="stretch"
                            isAnimatedOnMount={true}
                            style={stylesheet.chart}
                            config={orderConfig}
                        >
                            <BarChart
                                categoryFormatter={(value) => dayFormat.format(new Date(String(value)))}
                                valueFormatter={(value) => integer.format(value)}
                                categoryTitle={t("blocks.dashboard02.week")}
                                stackBehaviour="stacked"
                                categoryKey="date"
                                plotSize="medium"
                                seriesKeys={[
                                    "delivered",
                                    "shipping",
                                    "preparing",
                                    "returned"
                                ]}
                                data={orderRows}
                            />
                        </Chart>,
                        <Card
                            spreadBehaviour="stretch"
                            style={stylesheet.chart}
                            variant="outline"
                        >
                            <Card.Header>
                                <Card.Title>
                                    {t("blocks.dashboard02.activityTitle")}
                                </Card.Title>
                                <Card.Description>
                                    {t("blocks.dashboard02.activityDescription")}
                                </Card.Description>
                            </Card.Header>
                            <Card.Content>
                                <Timeline
                                    accessibilityLabel={t("blocks.dashboard02.activityTitle")}
                                    timeBehaviour="absolute"
                                >
                                    <Timeline.Item
                                        description={t("blocks.dashboard02.activityPaymentDetail", [
                                            "#NB-20418",
                                            currency.format(2480)
                                        ])}
                                        title={t("blocks.dashboard02.activityPayment")}
                                        time={new Date(2026, 10, 1, 14, 32)}
                                        icon={CircleCheckIcon}
                                    />
                                    <Timeline.Item
                                        description={t("blocks.dashboard02.activityRefundDetail", [
                                            "#NB-20391",
                                            currency.format(640)
                                        ])}
                                        title={t("blocks.dashboard02.activityRefund")}
                                        time={new Date(2026, 10, 1, 13, 5)}
                                        icon={UndoIcon}
                                    />
                                    <Timeline.Item
                                        description={t("blocks.dashboard02.productSantos")}
                                        title={t("blocks.dashboard02.activityStock")}
                                        time={new Date(2026, 10, 1, 11, 48)}
                                        icon={TriangleAlertIcon}
                                        type="warning"
                                    />
                                    <Timeline.Item
                                        description={t("blocks.dashboard02.customerName")}
                                        title={t("blocks.dashboard02.activityCustomer")}
                                        time={new Date(2026, 10, 1, 10, 20)}
                                        icon={UserPlusIcon}
                                    />
                                    <Timeline.Item
                                        description={t("blocks.dashboard02.activityInvoiceDetail", [
                                            "F-2026-0187",
                                            t("blocks.dashboard02.invoiceCustomer")
                                        ])}
                                        title={t("blocks.dashboard02.activityInvoice")}
                                        time={new Date(2026, 10, 1, 9, 12)}
                                        icon={FileTextIcon}
                                    />
                                </Timeline>
                            </Card.Content>
                        </Card>,
                        stylesheet.two,
                        stylesheet.one
                    )
                }
                <Card
                    spreadBehaviour="stretch"
                    variant="outline"
                >
                    <Card.Header>
                        <Card.Title>
                            {t("blocks.dashboard02.stockTitle")}
                        </Card.Title>
                        <Card.Description>
                            {t("blocks.dashboard02.stockDescription")}
                        </Card.Description>
                    </Card.Header>
                    <Card.Content>
                        <Alert
                            description={t("blocks.dashboard02.stockNoticeBody")}
                            title={t("blocks.dashboard02.stockNoticeTitle")}
                            actions={[
                                {
                                    title: t("blocks.dashboard02.reorder"),
                                    onPress: handlePress
                                }
                            ]}
                            icon={TriangleAlertIcon}
                            type="warning"
                        />
                    </Card.Content>
                    <Card.Content>
                        <Table
                            caption={t("blocks.dashboard02.stockTitle")}
                            isCaptionHidden={true}
                        >
                            <Table.Header>
                                <Table.Row>
                                    <Table.Head>
                                        {t("blocks.dashboard02.columnProduct")}
                                    </Table.Head>
                                    <Table.Head>
                                        {t("blocks.dashboard02.columnSku")}
                                    </Table.Head>
                                    <Table.Head
                                        isNumeric={true}
                                    >
                                        {t("blocks.dashboard02.columnLeft")}
                                    </Table.Head>
                                    <Table.Head
                                        isNumeric={true}
                                    >
                                        {t("blocks.dashboard02.columnRate")}
                                    </Table.Head>
                                    <Table.Head>
                                        {t("blocks.dashboard02.columnRunOut")}
                                    </Table.Head>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {STOCK.map(renderStock)}
                            </Table.Body>
                        </Table>
                    </Card.Content>
                </Card>
            </View>
        </Sidebar.Inset>
    </Sidebar.Provider>;
};
export default Dashboard02;
